import { createElement } from 'react';
import { icons } from 'lucide-react';
import { getPluginIcon } from '@/lib/plugin-registry';

const iconLookup = icons as Record<string, React.ComponentType<{ className?: string }>>;

/**
 * Renders an icon from its PascalCase string name (e.g. `"Code"`, `"Star"`,
 * `"UserCircle"`). Resolution order:
 *
 * 1. Plugin registry — covers icons each plugin registers in its
 *    `web/register.ts`. This includes custom SVGs and Lucide icons whose
 *    name in `lucide-react`'s `icons` map differs from the plugin's
 *    declared name (e.g. `UserCircle` is exported as `CircleUserRound`
 *    in newer Lucide releases but plugins still ship under the old
 *    name).
 * 2. Lucide stock icons — fallback for icons that no plugin has
 *    registered explicitly.
 * 3. Monospace text fallback — last resort so a missing registration
 *    is visible to whoever owns the plugin instead of silently rendering
 *    nothing.
 */
export function LucideIconByName({
  name,
  className = 'h-3.5 w-3.5',
}: {
  name: string;
  className?: string;
}) {
  const pluginIcon = getPluginIcon(name);
  if (pluginIcon) {
    return (
      <span
        data-testid="lucide-icon"
        data-icon-name={name}
        data-icon-source="plugin"
        className="inline-flex"
      >
        {createElement(pluginIcon, { className })}
      </span>
    );
  }
  const StockIcon = iconLookup[name];
  if (StockIcon) {
    return (
      <span
        data-testid="lucide-icon"
        data-icon-name={name}
        data-icon-source="lucide"
        className="inline-flex"
      >
        <StockIcon className={className} />
      </span>
    );
  }
  return (
    <span
      data-testid="lucide-icon-fallback"
      data-icon-name={name}
      className="font-mono text-xs text-muted-foreground"
    >
      {name}
    </span>
  );
}
